<script setup lang="ts">
const props = defineProps({ blok: Object })

const mapItems = ref([
    {
        label: props.blok?.north_america_label,
        data: props.blok?.north_america_data,
        position: {
            left: '10%',
            top: '-50px',
        },
        circle: {
            width: '55px',
            height: '55px',
            fontSize: '30px',
        },
        text: {
            width: '135px',
        },
        line: {
            height: '110px',
        },
    },
    {
        label: props.blok?.europe_label,
        data: props.blok?.europe_data,
        position: {
            left: '40%',
            top: '-20%',
        },
        circle: {
            width: '230px',
            height: '230px',
            fontSize: '54px',
        },
        text: {
            width: '300px',
        },
        line: {
            height: '45px',
        },
    },
    {
        label: props.blok?.asia_label,
        data: props.blok?.asia_data,
        position: {
            left: '70%',
            top: '25%',
        },
        circle: {
            width: '75px',
            height: '75px',
            fontSize: '32px',
        },
        text: {
            width: '300px',
        },
        line: {
            height: '45px',
        },
    },
    {
        label: props.blok?.africa_label,
        data: props.blok?.africa_data,
        position: {
            left: '52%',
            top: '50%',
        },
        circle: {
            width: '55px',
            height: '55px',
            fontSize: '30px',
        },
        text: {
            width: '135px',
        },
        line: {
            height: '110px',
        },
    },
    {
        label: props.blok?.australia_label,
        data: props.blok?.australia_data,
        position: {
            left: '85%',
            top: '50%',
        },
        circle: {
            width: '55px',
            height: '55px',
            fontSize: '30px',
        },
        text: {
            width: '135px',
        },
        line: {
            height: '110px',
        },
    },
])

const mapResume = computed(() => {
    return [
        {
            data: props.blok?.participants_data,
            label: props.blok?.participants_label,
        },
        {
            data: props.blok?.big_companies_data,
            label: props.blok?.big_companies_label,
        },
        {
            data: props.blok?.small_companies_data,
            label: props.blok?.small_companies_label,
        },
        {
            data: props.blok?.retailers_data,
            label: props.blok?.retailers_label,
        },

    ]
})

const mapHighlights = ref([
    {
        open: false,
        id: 'big',
        ico: props.blok?.big_companies_highlight_icon,
        label: props.blok?.big_companies_highlight_label,
        content: props.blok?.big_companies_highlight_content,
        bg: 'light',
    },
    {
        open: false,
        id: 'retailer',
        ico: props.blok?.retailer_companies_highlight_icon,
        label: props.blok?.retailer_companies_highlight_label,
        content: props.blok?.retailer_companies_highlight_content,
        bg: 'dark',
    },
    {
        open: false,
        id: 'small',
        ico: props.blok?.small_companies_highlight_icon,
        label: props.blok?.small_companies_highlight_label,
        content: props.blok?.small_companies_highlight_content,
        bg: 'light',
    },
])
</script>

<template>
    <section class="section bg-chsc-raw">
        <UiContainer max-width="max-w-4xl">
            <header class="insight-category-header">
                <StoryblokRichText :doc="blok?.header" />
            </header>
        </UiContainer>
        <UiContainer
            max-width="max-w-5xl"
            class="mt-44"
        >
            <div class="inline-flex relative max-[1025px]:flex-wrap">
                <img
                    :src="blok?.map.filename"
                    :alt="blok?.map.alt"
                    class="w-full"
                >
                <div
                    v-for="item in mapItems"
                    :key="item.label"
                    :class="[
                        'absolute flex flex-col items-center max-[1025px]:relative max-[1025px]:left-0! max-[1025px]:top-0! max-[1025px]:w-1/2 max-[1025px]:h-full max-[1025px]:p-6 max-[1025px]:items-center max-[1025px]:hidden',
                    ]"
                    :style="{
                        left: item.position.left,
                        top: item.position.top,
                    }"
                >
                    <div
                        :class="[
                            'flex items-center justify-center rounded-full bg-chsc-indigo-500 max-[1025px]:relative max-[1025px]:left-0! max-[1025px]:top-0! max-[1025px]:w-[50px]! max-[1025px]:h-[50px]!',
                        ]"
                        :style="{
                            width: item.circle.width,
                            height: item.circle.height,
                        }"
                    >
                        <span
                            :class="[`text-chsc-yellow font-semibold max-[1025px]:text-sm!`]"
                            :style="{
                                fontSize: item.circle.fontSize,
                            }"
                        >{{ item.data }}</span>
                        <div
                            class="absolute left-full top-3 translate-x-2 [&>p]:text-xs [&>p]:leading-none [&>p>strong]:font-semibold max-[1025px]:relative max-[1025px]:tip"
                            :style="{ width: item.text.width }"
                        >
                            <StoryblokRichText :doc="item.label" />
                        </div>
                    </div>
                    <div
                        :class="[
                            'w-px bg-black',
                        ]"
                        :style="{
                            height: item.line.height,
                        }"
                    />
                </div>
                <div
                    v-motion-slide-visible-once-top
                    class="absolute left-6 bottom-6 max-w-[170px] bg-chsc-indigo-500 rounded-[18px] p-3 text-center text-chsc-yellow [&>p]:font-londrina-solid [&>p]:fs-xl [&>p>strong]:fs-6xl [&>p>strong]:font-normal max-[1025px]:left-1/2 max-[1025px]:bottom-initial max-[1025px]:top-1/2 max-[1025px]:-translate-y-1/2 max-[1025px]:-translate-x-1/2 max-[641px]:relative max-[641px]:left-0! max-[641px]:top-0! max-[641px]:w-full max-[641px]:h-full max-[641px]:translate-x-0 max-[641px]:translate-y-0 max-[641px]:mx-auto max-[641px]:mb-8 max-[641px]:-mt-8"
                >
                    <StoryblokRichText :doc="blok?.map_highlight" />
                </div>
            </div>
        </UiContainer>
        <UiContainer max-width="max-w-4xl">
            <div class="flex items-center gap-x-3 rounded-[40px] border border-gray-200 bg-white py-3 px-6 max-[640px]:flex-col max-[640px]:items-start">
                <div
                    v-for="item in mapResume"
                    :key="item.label"
                    class="relative flex gap-x-3 flex-1 items-center after:content-[''] after:w-px after:bg-gray-200 after:h-[15px] after:absolute after:right-3 after:top-1/2 after:-translate-y-1/2 pr-3 last:after:hidden"
                >
                    <p class="fs-3xl">
                        {{ item.data }}
                    </p>
                    <p class="fs-sm text-chsc-indigo-500">
                        {{ item.label }}
                    </p>
                </div>
            </div>
        </UiContainer>
        <UiContainer
            max-width="max-w-5xl"
            class="max-[1025px]:mt-20"
        >
            <div class="mt-20 flex items-center justify-center gap-x-10 max-[1025px]:flex-wrap max-[1025px]:gap-0 max-[1025px]:-m-6">
                <div
                    v-for="item in mapHighlights"
                    :key="item.label"
                    class="flex flex-1 flex-col items-center gap-y-3 max-[1025px]:w-1/2 max-[1025px]:flex-initial max-[1025px]:p-6 max-[641px]:w-full"
                >
                    <div
                        v-motion-slide-visible-once-top
                        :class="[
                            `group w-full h-[336px] flex flex-col rounded-lg shadow-lg pt-8 px-6 pb-2.5 hover:shadow-xl transition-shadow duration-150 cursor-pointer`,
                            { 'bg-chsc-indigo-500 text-white': item.bg === 'dark' },
                            { 'bg-chsc-yellow text-chsc-indigo-500': item.bg === 'light' },
                        ]"
                        @click="item.open = true"
                    >
                        <div class="h-[130px] flex items-center justify-center">
                            <img
                                :src="item.ico.filename"
                            >
                        </div>
                        <p class="mt-6 fs-3xl text-center font-eb-garamond">
                            {{ item.label }}
                        </p>
                        <div class="flex-1 flex items-end justify-center">
                            <span class="mt-2 w-10 h-10 rounded-full border border-white text-white flex items-center justify-center fs-5xl font-light group-hover:rotate-90 transition-transform duration-150 cursor-pointer">
                                +
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </UiContainer>
        <UiContainer
            max-width="max-w-4xl"
            class="mt-32"
        >
            <div class="global-text">
                <StoryblokRichText :doc="blok?.confidentiality" />
            </div>
        </UiContainer>
        <UModal
            v-for="modal in mapHighlights"
            :key="modal.id"
            :open="modal.open"
            :title="modal.label"
            dismissible
            :description="modal.label"
            :ui="{
                content: 'border border-chsc-indigo-500',
            }"
            class="max-w-4xl sm:max-w-4xl rounded-[40px] overflow-hidden"
        >
            <template #content>
                <div>
                    <header
                        :class="[
                            `py-3.5 pl-20 pr-6 border-b border-chsc-indigo-500 flex items-center justify-between`,
                            { 'bg-chsc-indigo-500 text-white': modal.bg === 'dark' },
                            { 'bg-chsc-yellow text-chsc-indigo-500': modal.bg === 'light' },
                        ]"
                    >
                        <h3 class="fs-3xl font-eb-garamond font-medium">
                            {{ modal.label }}
                        </h3>
                        <button
                            class="flex items-center justify-center fs-xl cursor-pointer"
                            @click="modal.open = false"
                        >
                            <UIcon name="i-heroicons-x-mark" />
                        </button>
                    </header>
                    <div class="p-20 max-h-[75vh] overflow-y-auto global-text">
                        <StoryblokRichText :doc="modal.content" />
                    </div>
                </div>
            </template>
        </UModal>
    </section>
</template>
